import React, { useEffect, useState } from 'react';
import { Sparkles, Mail, MessageSquare, Users, TrendingUp, Activity } from 'lucide-react';
import {
  getCommsIntelKpis,
  getEmailLogStats,
  getSmsLogStats,
  getCommunicationsOutbox,
  getGuests,
} from '../../../data/api';
import { Loading } from './CiShared';
import { rsvpIsYes, rsvpIsNo } from './ciUtils';
import './analyticsScreen.css';

// ── Communication Analytics ──────────────────────────────────────────────────
// Every number on this screen is REAL — computed from existing endpoints. The
// charts (donut, grouped bars, horizontal bars, strength ring) are hand-built
// with inline SVG / divs — no chart library, no fabricated series. Each metric
// names its own data source and has a graceful zero state, so an event with no
// activity yet reads honestly rather than blank.
//
// Sources:
//   · getEmailLogStats()        → {total, sent, delivered, failed, bounced, opened, clicked}
//   · getSmsLogStats()          → {total, sms, mms, failed}
//   · getCommunicationsOutbox() → {items[], health:{total, sent, pending, failed, ...}}
//   · getCommsIntelKpis()       → {guests_engaged, response_rate, raw:{engaged_by_signal,...}}
//   · getGuests()               → [{rsvp_status, ...}]   (bucketed yes / pending / no)

// RSVP classification uses the canonical ciUtils helpers (single source of truth,
// mirrored to backend server.py RSVP_*_TOKENS). Anything not Yes/No is "pending".

// Palette pulled from the shared CI custom properties so colours stay on-brand.
const C = {
  green: 'var(--ci-green)',
  gold: 'var(--ci-gold-hi)',
  goldDeep: 'var(--ci-gold)',
  blue: 'var(--ci-blue)',
  muted: 'rgba(255,255,255,.2)',
};

function pct(n, d) {
  if (!d || d <= 0) return null;
  return Math.round((Number(n || 0) / Number(d)) * 100);
}

function Metric({ value, sub, label, note, icon }) {
  return (
    <div className="ci-kpi">
      {icon ? <div className="ci-ic">{icon}</div> : null}
      <div className="ci-v">{value}{sub ? <small> {sub}</small> : null}</div>
      <div className="ci-l">{label}</div>
      {note ? <div className="ci-t ci-muted">{note}</div> : null}
    </div>
  );
}

function Panel({ title, icon, children, foot }) {
  return (
    <div className="ci-glass" style={{ padding: 20, marginBottom: 16 }}>
      <div className="ci-ins-h" style={{ marginBottom: 14 }}>{icon} {title}</div>
      {children}
      {foot ? <p style={{ fontSize: 11.5, color: 'var(--ci-txt3)', marginTop: 14, lineHeight: 1.5 }}>{foot}</p> : null}
    </div>
  );
}

function EmptyLine({ children }) {
  return <div className="ci-empty" style={{ textAlign: 'left', padding: '14px 2px' }}>{children}</div>;
}

// ── Donut chart ───────────────────────────────────────────────────────────────
// Pure-SVG ring built from stroke-dasharray arcs. `segments` is [{value,color}].
// The circumference is split proportionally; a zero total renders a flat track.
function Donut({ segments, total, centerValue, centerLabel, size = 168, stroke = 18 }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const sum = segments.reduce((a, s) => a + Number(s.value || 0), 0);
  let offset = 0;
  return (
    <div className="an-donut" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img"
        aria-label={`${centerLabel}: ${centerValue}`}>
        {/* base track */}
        <circle cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke="rgba(255,255,255,.07)" strokeWidth={stroke} />
        {sum > 0 && segments.map((s, i) => {
          const frac = Number(s.value || 0) / sum;
          const dash = frac * circ;
          const seg = (
            <circle key={i} cx={size / 2} cy={size / 2} r={r} fill="none"
              stroke={s.color} strokeWidth={stroke} strokeLinecap="butt"
              strokeDasharray={`${dash} ${circ - dash}`}
              strokeDashoffset={-offset} />
          );
          offset += dash;
          return seg;
        })}
      </svg>
      <div className="an-donut-center">
        <span className="an-big">{centerValue}</span>
        <span className="an-cap">{centerLabel}</span>
      </div>
    </div>
  );
}

// ── Strength ring ─────────────────────────────────────────────────────────────
// Single-value progress ring (0–100%). Used for the engaged-share readout.
function Ring({ percent, color = C.gold, size = 96, stroke = 11 }) {
  const p = Math.max(0, Math.min(100, Number(percent || 0)));
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (p / 100) * circ;
  return (
    <div className="an-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`${p}%`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,.08)" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={`${dash} ${circ - dash}`} />
      </svg>
      <div className="an-ring-c">
        <b>{p}%</b>
        <span>ENGAGED</span>
      </div>
    </div>
  );
}

// ── Grouped vertical bars ─────────────────────────────────────────────────────
// `groups` = [{ label, sub, icon, bars:[{ key, value, max, color, na }] }].
// Heights are taken against a single shared `max` so email and SMS are visually
// comparable on the same axis. `na:true` renders a vertical "n/a" note instead
// of inventing a value (used for SMS opens/clicks, which carriers never report).
function GroupedBars({ groups, axisMax }) {
  const safeMax = axisMax > 0 ? axisMax : 1;
  return (
    <div className="an-grouped">
      {groups.map((g) => (
        <div className="an-group" key={g.label}>
          <div className="an-group-bars">
            {g.bars.map((b) => {
              const h = b.na ? 0 : Math.round((Number(b.value || 0) / safeMax) * 100);
              return (
                <div className="an-bar-col" key={b.key} title={`${b.key}: ${b.na ? 'not reported' : b.value}`}>
                  {b.na
                    ? <span className="an-bar-na">n/a</span>
                    : <span className="an-bar-val">{b.value}</span>}
                  <div className="an-bar"
                    style={{
                      height: `${b.na ? 2 : Math.max(2, h)}%`,
                      background: b.na ? 'repeating-linear-gradient(45deg, rgba(255,255,255,.05), rgba(255,255,255,.05) 4px, transparent 4px, transparent 8px)' : b.color,
                      border: b.na ? '1px dashed var(--ci-bd2)' : 'none',
                    }} />
                </div>
              );
            })}
          </div>
          <div className="an-group-label">{g.icon} {g.label}</div>
          {g.sub ? <div className="an-group-sub">{g.sub}</div> : null}
        </div>
      ))}
    </div>
  );
}

export default function AnalyticsScreen() {
  const [kpis, setKpis] = useState(null);
  const [emailStats, setEmailStats] = useState(null);
  const [smsStats, setSmsStats] = useState(null);
  const [outbox, setOutbox] = useState(null);
  const [guests, setGuests] = useState(null);
  const [loading, setLoading] = useState(true);
  const [partial, setPartial] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.allSettled([
      getCommsIntelKpis(),
      getEmailLogStats(),
      getSmsLogStats(),
      getCommunicationsOutbox({ limit: 250 }),
      getGuests(),
    ])
      .then(([k, e, s, o, g]) => {
        if (!alive) return;
        let any = false;
        if (k.status === 'fulfilled') setKpis(k.value); else any = true;
        if (e.status === 'fulfilled') setEmailStats(e.value); else any = true;
        if (s.status === 'fulfilled') setSmsStats(s.value); else any = true;
        if (o.status === 'fulfilled') setOutbox(o.value); else any = true;
        if (g.status === 'fulfilled') setGuests(Array.isArray(g.value) ? g.value : []); else any = true;
        setPartial(any);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) return <div className="ci-view"><Loading label="Computing analytics…" /></div>;

  // ── Email engagement (open / click / delivery) ──────────────────────────────
  // Rate denominator is total email-log rows; we surface the basis under each.
  const eTotal = Number(emailStats?.total ?? 0);
  const eOpened = Number(emailStats?.opened ?? 0);
  const eClicked = Number(emailStats?.clicked ?? 0);
  const eDelivered = Number(emailStats?.delivered ?? 0);
  const eFailed = Number(emailStats?.failed ?? 0) + Number(emailStats?.bounced ?? 0);
  const openRate = pct(eOpened, eTotal);
  const clickRate = pct(eClicked, eTotal);
  const deliveryRate = pct(eDelivered, eTotal);

  // ── SMS (delivery only — the schema records no opens/clicks for SMS) ─────────
  const sTotal = Number(smsStats?.total ?? 0);
  const sFailed = Number(smsStats?.failed ?? 0);
  const sDelivered = Math.max(0, sTotal - sFailed);
  const sDeliveryRate = pct(sDelivered, sTotal);

  // ── Sent volume + statuses (from the unified outbox health roll-up) ──────────
  const health = outbox?.health || null;
  const sentVolume = health ? Number(health.sent ?? 0) : null;
  const outboxTotal = health ? Number(health.total ?? 0) : null;

  // ── RSVP conversion funnel (bucket guests by rsvp_status) ───────────────────
  let funnel = null;
  if (guests) {
    let yes = 0, no = 0, pending = 0;
    for (const g of guests) {
      const raw = String(g?.rsvp_status ?? '').trim().toLowerCase();
      if (!raw) { pending += 1; continue; }
      if (rsvpIsYes(raw)) yes += 1;
      else if (rsvpIsNo(raw)) no += 1;
      else pending += 1; // unknown token → awaiting a clean response
    }
    funnel = { total: guests.length, yes, no, pending };
  }
  const conversionRate = funnel ? pct(funnel.yes, funnel.total) : null;

  // ── Attendance forecast (confirmed + conservative projection) ───────────────
  // Confirmed = guests already at YES. Projected adds a CONSERVATIVE estimate of
  // how many of the still-pending guests will convert, using the measured email
  // open rate as the conversion proxy (an opened message is the strongest real
  // signal we can attribute). Clearly labeled as an estimate; if we have no open
  // rate we show no projection rather than inventing one.
  let forecast = null;
  if (funnel) {
    const confirmed = funnel.yes;
    const hasProjBasis = openRate != null && funnel.pending > 0;
    const projectedFromPending = hasProjBasis ? Math.round(funnel.pending * (openRate / 100)) : 0;
    forecast = {
      confirmed,
      projectedFromPending,
      estimateTotal: confirmed + projectedFromPending,
      hasProjBasis,
      pending: funnel.pending,
    };
  }

  // ── Engagement signals (exact live counts from the KPI intel) ───────────────
  const raw = kpis?.raw || {};
  const totalGuests = raw.total_guests ?? (funnel ? funnel.total : null);
  const signals = raw.engaged_by_signal || null;
  const respRate = kpis?.response_rate != null ? Math.round(kpis.response_rate * 100) : null;
  const engaged = kpis?.guests_engaged ?? null;
  const engagedPct = (engaged != null && totalGuests) ? pct(engaged, totalGuests) : null;

  const noEmailData = eTotal === 0;
  const noSmsData = sTotal === 0;

  // Shared axis for the grouped channel chart so email & SMS share one scale.
  const channelAxisMax = Math.max(eDelivered, eOpened, eClicked, sDelivered, 1);

  return (
    <div className="ci-view">
      {/* Headline rates — all real, each with its basis */}
      <div className="ci-kpis" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 16 }}>
        <Metric
          icon={<Mail size={14} />}
          value={openRate != null ? openRate : '—'}
          sub={openRate != null ? '%' : ''}
          label="Overall Open Rate"
          note={noEmailData ? 'No emails sent yet' : `${eOpened} opened of ${eTotal} emails`}
        />
        <Metric
          icon={<Sparkles size={14} />}
          value={clickRate != null ? clickRate : '—'}
          sub={clickRate != null ? '%' : ''}
          label="Click Rate"
          note={noEmailData ? 'No emails sent yet' : `${eClicked} clicked of ${eTotal} emails`}
        />
        <Metric
          icon={<TrendingUp size={14} />}
          value={conversionRate != null ? conversionRate : '—'}
          sub={conversionRate != null ? '%' : ''}
          label="RSVP Conversion"
          note={funnel ? `${funnel.yes} confirmed of ${funnel.total} invited` : 'No guests yet'}
        />
        <Metric
          icon={<Users size={14} />}
          value={engagedPct != null ? engagedPct : (engaged ?? '—')}
          sub={engagedPct != null ? '%' : ''}
          label="Guests Engaged"
          note={totalGuests != null ? `${engaged ?? 0} of ${totalGuests} guests` : 'No engagement signals yet'}
        />
      </div>

      {/* Metric-trust disclosure — explicit about what is exact vs webhook-measured vs estimated. */}
      <div className="ci-glass" style={{ padding: '10px 14px', marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 10, borderColor: 'var(--ci-bdgold)' }}>
        <Activity size={14} style={{ color: 'var(--ci-gold-hi)', flex: 'none', marginTop: 2 }} />
        <span style={{ fontSize: 12, color: 'var(--ci-txt2)', lineHeight: 1.5 }}>
          <b style={{ color: 'var(--ci-txt)' }}>How to read these:</b> RSVP conversion, sent / failed counts, website visits and replies are <b>exact</b>. Email <b>open, click, delivered and bounced</b> figures are measured by the email delivery webhook — they read 0 until it's configured in production. SMS <b>"delivered"</b> is an estimate (sent − failed; carriers don't confirm individual delivery); SMS opens/clicks are never reported ("n/a").
        </span>
      </div>

      {partial && (
        <div className="ci-glass" style={{ padding: 16, marginBottom: 16 }}>
          <EmptyLine>Some analytics couldn't be loaded just now. Every figure shown is computed only from the data that was available.</EmptyLine>
        </div>
      )}

      {/* Two-up: RSVP donut + channel grouped bars */}
      <div className="an-charts">
        {/* RSVP conversion funnel — real donut from guest rsvp_status */}
        <div className="ci-glass" style={{ padding: 20 }}>
          <div className="ci-ins-h" style={{ marginBottom: 16 }}><TrendingUp size={15} /> RSVP conversion funnel</div>
          {funnel && funnel.total > 0 ? (
            <div className="an-donut-wrap">
              <Donut
                total={funnel.total}
                centerValue={`${conversionRate ?? 0}%`}
                centerLabel="confirmed"
                segments={[
                  { value: funnel.yes, color: C.green },
                  { value: funnel.pending, color: C.goldDeep },
                  { value: funnel.no, color: C.blue },
                ]}
              />
              <div className="an-legend">
                <div className="an-leg-row">
                  <span className="an-leg-dot" style={{ background: C.green }} />
                  <span className="an-leg-name">Confirmed — Yes</span>
                  <span className="an-leg-val">{funnel.yes}</span>
                  <span className="an-leg-pct">{pct(funnel.yes, funnel.total) ?? 0}%</span>
                </div>
                <div className="an-leg-row">
                  <span className="an-leg-dot" style={{ background: C.goldDeep }} />
                  <span className="an-leg-name">Awaiting response</span>
                  <span className="an-leg-val">{funnel.pending}</span>
                  <span className="an-leg-pct">{pct(funnel.pending, funnel.total) ?? 0}%</span>
                </div>
                <div className="an-leg-row">
                  <span className="an-leg-dot" style={{ background: C.blue }} />
                  <span className="an-leg-name">Declined — No</span>
                  <span className="an-leg-val">{funnel.no}</span>
                  <span className="an-leg-pct">{pct(funnel.no, funnel.total) ?? 0}%</span>
                </div>
                <div className="an-leg-row" style={{ borderTop: '1px solid var(--ci-bd)', paddingTop: 9, marginTop: 1 }}>
                  <span className="an-leg-dot" style={{ background: 'transparent', border: '1px solid var(--ci-bd2)' }} />
                  <span className="an-leg-name">Invited (all guests)</span>
                  <span className="an-leg-val">{funnel.total}</span>
                  <span className="an-leg-pct">100%</span>
                </div>
              </div>
            </div>
          ) : (
            <EmptyLine>No guests on the list yet — the funnel will populate as guests are added and respond.</EmptyLine>
          )}
          <p style={{ fontSize: 11.5, color: 'var(--ci-txt3)', marginTop: 16, lineHeight: 1.5 }}>
            Ring segments are your real guest records by RSVP status. “Awaiting response” includes anyone not yet marked Yes or No.
          </p>
        </div>

        {/* Channel performance — grouped bars, email vs SMS on one axis */}
        <div className="ci-glass" style={{ padding: 20 }}>
          <div className="ci-ins-h" style={{ marginBottom: 16 }}><Activity size={15} /> Channel performance</div>
          {(noEmailData && noSmsData) ? (
            <EmptyLine>Nothing sent yet — channel performance appears once you send your first email or text.</EmptyLine>
          ) : (
            <>
              <GroupedBars
                axisMax={channelAxisMax}
                groups={[
                  {
                    label: 'Email',
                    icon: <Mail size={13} />,
                    sub: noEmailData ? 'none sent' : `${eTotal} events · ${deliveryRate ?? 0}% delivered`,
                    bars: noEmailData
                      ? [
                          { key: 'Delivered', value: 0, color: C.green },
                          { key: 'Opened', value: 0, color: C.gold },
                          { key: 'Clicked', value: 0, color: C.goldDeep },
                        ]
                      : [
                          { key: 'Delivered', value: eDelivered, color: C.green },
                          { key: 'Opened', value: eOpened, color: C.gold },
                          { key: 'Clicked', value: eClicked, color: C.goldDeep },
                        ],
                  },
                  {
                    label: 'SMS',
                    icon: <MessageSquare size={13} />,
                    sub: noSmsData ? 'none sent' : `${sTotal} texts · ${sDeliveryRate ?? 0}% delivered`,
                    bars: [
                      { key: 'Delivered', value: noSmsData ? 0 : sDelivered, color: C.green },
                      { key: 'Opened', na: true },
                      { key: 'Clicked', na: true },
                    ],
                  },
                ]}
              />
              <div className="an-chips">
                <span className="an-chip"><i style={{ background: C.green }} /> Delivered</span>
                <span className="an-chip"><i style={{ background: C.gold }} /> Opened</span>
                <span className="an-chip"><i style={{ background: C.goldDeep }} /> Clicked</span>
                <span className="an-chip" style={{ color: 'var(--ci-txt3)' }}>n/a = carriers don’t report it for SMS</span>
              </div>
            </>
          )}
          <p style={{ fontSize: 11.5, color: 'var(--ci-txt3)', marginTop: 14, lineHeight: 1.5 }}>
            Bars share one scale so channels compare directly. SMS opens & clicks are marked “n/a” — carriers never report them, so no engagement number is invented.
          </p>
        </div>
      </div>

      {/* Delivery detail + outbox roll-up — exact counts behind the bars above */}
      {(!noEmailData || !noSmsData || sentVolume != null) && (
        <Panel title="Delivery detail" icon={<MessageSquare size={15} />}
          foot="Exact counts behind the channel chart, straight from the email- and SMS-log stats.">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10, fontSize: 12.5, fontWeight: 700, color: 'var(--ci-txt2)' }}>
                <Mail size={14} /> Email
              </div>
              {noEmailData ? (
                <EmptyLine>No emails sent yet.</EmptyLine>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 18px' }}>
                  <Stat label="Delivered" value={eDelivered} pctv={deliveryRate} color={C.green} />
                  <Stat label="Opened" value={eOpened} pctv={openRate} color={C.gold} />
                  <Stat label="Clicked" value={eClicked} pctv={clickRate} color={C.goldDeep} />
                  <Stat label="Failed / bounced" value={eFailed} pctv={pct(eFailed, eTotal)} color="var(--ci-txt3)" />
                  <div style={{ gridColumn: '1 / -1', fontSize: 11, color: 'var(--ci-txt3)', marginTop: 2 }}>{eTotal} total email events</div>
                </div>
              )}
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10, fontSize: 12.5, fontWeight: 700, color: 'var(--ci-txt2)' }}>
                <MessageSquare size={14} /> SMS
              </div>
              {noSmsData ? (
                <EmptyLine>No texts sent yet.</EmptyLine>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 18px' }}>
                  <Stat label="Delivered" value={sDelivered} pctv={sDeliveryRate} color={C.green} />
                  <Stat label="Failed" value={sFailed} pctv={pct(sFailed, sTotal)} color="var(--ci-txt3)" />
                  <div style={{ gridColumn: '1 / -1', fontSize: 11, color: 'var(--ci-txt3)', marginTop: 2 }}>
                    {sTotal} total texts{Number(smsStats?.mms ?? 0) > 0 ? ` · ${smsStats.mms} MMS` : ''} · opens & clicks aren’t reported for SMS
                  </div>
                </div>
              )}
            </div>
          </div>
          {sentVolume != null && (
            <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--ci-bd)', display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: 'var(--ci-txt2)' }}>
                <b style={{ color: 'var(--ci-gold-hi)', fontSize: 15 }}>{sentVolume}</b> sent communications
              </span>
              {health.needs_attention > 0 && (
                <span style={{ fontSize: 12, color: 'var(--ci-txt2)' }}>
                  <b style={{ color: 'var(--ci-gold-hi)', fontSize: 15 }}>{health.needs_attention}</b> need attention
                </span>
              )}
              <span style={{ fontSize: 11.5, color: 'var(--ci-txt3)', alignSelf: 'center' }}>
                across {outboxTotal} outbox item{outboxTotal === 1 ? '' : 's'}
              </span>
            </div>
          )}
        </Panel>
      )}

      {/* Attendance forecast — confirmed + clearly-labeled conservative estimate */}
      <Panel title="Attendance forecast" icon={<Users size={15} />}
        foot={forecast && forecast.hasProjBasis
          ? `Estimate basis: ${forecast.pending} pending guest${forecast.pending === 1 ? '' : 's'} × ${openRate}% email open rate. Open rate is used as a conservative conversion proxy — treat the projection as a planning estimate, not a guarantee.`
          : 'A projection appears once there are pending guests and a measured email open rate to estimate from. Until then only confirmed Yes responses are shown — nothing is guessed.'}>
        {forecast ? (
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'stretch' }}>
            <div style={{ flex: '1 1 150px', padding: '16px 18px', borderRadius: 12, background: 'rgba(103,194,142,.08)', border: '1px solid var(--ci-bd)' }}>
              <div style={{ fontSize: 30, fontWeight: 700, color: 'var(--ci-green)' }}>{forecast.confirmed}</div>
              <div style={{ fontSize: 12, color: 'var(--ci-txt2)', marginTop: 2 }}>Confirmed attending</div>
              <div style={{ fontSize: 11, color: 'var(--ci-txt3)', marginTop: 4 }}>Guests already at “Yes”</div>
            </div>
            <div style={{ flex: '1 1 150px', padding: '16px 18px', borderRadius: 12, background: 'rgba(198,167,90,.08)', border: '1px solid var(--ci-bd)' }}>
              <div style={{ fontSize: 30, fontWeight: 700, color: 'var(--ci-gold-hi)' }}>
                {forecast.hasProjBasis ? `~${forecast.estimateTotal}` : forecast.confirmed}
              </div>
              <div style={{ fontSize: 12, color: 'var(--ci-txt2)', marginTop: 2 }}>Projected total <span style={{ color: 'var(--ci-txt3)' }}>(estimate)</span></div>
              <div style={{ fontSize: 11, color: 'var(--ci-txt3)', marginTop: 4 }}>
                {forecast.hasProjBasis
                  ? `Confirmed + ~${forecast.projectedFromPending} of ${forecast.pending} pending`
                  : 'No projection basis yet'}
              </div>
            </div>
          </div>
        ) : (
          <EmptyLine>Add guests to see a confirmed count and an attendance estimate.</EmptyLine>
        )}
      </Panel>

      {/* Engagement signals — exact live counts + a real engaged-share ring */}
      <Panel title="Engagement signals (live counts)" icon={<Sparkles size={15} />}
        foot="Visited the website and replied/messaged are exact. Opened an email and clicked a link depend on the email delivery webhook — they read 0 until it's configured. The ring shows the share of guests with at least one signal.">
        {signals ? (
          <div className="an-strength" style={{ alignItems: 'flex-start' }}>
            {engagedPct != null && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <Ring percent={engagedPct} />
                <div style={{ fontSize: 11, color: 'var(--ci-txt3)', textAlign: 'center', maxWidth: 110 }}>
                  {engaged ?? 0} of {totalGuests ?? 0} guests
                </div>
              </div>
            )}
            <div className="an-hbars" style={{ flex: 1, minWidth: 240 }}>
              {[
                ['Opened an email', signals.opened, C.gold],
                ['Clicked a link', signals.clicked, C.goldDeep],
                ['Visited the website', signals.visited, C.blue],
                ['Replied / messaged', signals.replied, C.green],
              ].map(([label, count, color]) => {
                const denom = totalGuests || 1;
                const w = Math.min(100, Math.round((Number(count || 0) / denom) * 100));
                return (
                  <div className="an-hrow" key={label}>
                    <span className="an-hl">{label}</span>
                    <div className="an-htrack">
                      <div className="an-hfill" style={{ width: `${w}%`, background: color }} />
                    </div>
                    <span className="an-hval"><b>{count ?? 0}</b>{totalGuests ? <small> · {w}%</small> : null}</span>
                  </div>
                );
              })}
              {respRate != null && (
                <div style={{ fontSize: 11.5, color: 'var(--ci-txt3)', marginTop: 4 }}>
                  Inbound response rate: <b style={{ color: 'var(--ci-txt2)' }}>{respRate}%</b>
                  {` (${raw.inbound_messages_handled ?? 0} handled of ${raw.inbound_messages_total ?? 0})`}
                </div>
              )}
            </div>
          </div>
        ) : (
          <EmptyLine>No engagement signals recorded yet — these populate as guests open, click, visit, or reply.</EmptyLine>
        )}
      </Panel>
    </div>
  );
}

// Compact stat cell for the delivery detail grid (count + its real percentage).
function Stat({ label, value, pctv, color }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
        <span style={{ fontSize: 20, fontWeight: 700, color }}>{value}</span>
        {pctv != null && <span style={{ fontSize: 12, color: 'var(--ci-txt3)' }}>{pctv}%</span>}
      </div>
      <span style={{ fontSize: 11.5, color: 'var(--ci-txt2)' }}>{label}</span>
    </div>
  );
}
